'use client'

import { useDialogActions } from '@/components/dialog'
import MD from '@/components/markdown'
import { Card } from '@/components/ui/card'
import { Flex, FlexCol } from '@/components/ui/flex'
import { useScreen } from '@/components/utils/windowSize'
import { getKoalendarUrl, siteConfig } from '@/config/site.config'
import { buildPhoneUrl, cn } from '@/lib/utils/utils'
import { FaPhone } from 'react-icons/fa'
import { FaCalendarDays } from 'react-icons/fa6'
import { getI18n, I18nDictionary, LangCode } from '../../i18n/lang-context'
import { Button } from '../../ui/button'
import { useLBCalendarDialog } from './lb-calendar-dialog'

const I18N: I18nDictionary = {
  action: {
    en: 'Book a Call',
    fr: 'Réserver un appel'
  },
  becomePartner: {
    en: 'Become a Partner →',
    fr: 'Devenez partenaire →'
  },
  call: {
    en: 'Call ***Legibloq team*** directly at',
    fr: 'Appelez directement ***l\'équipe Legibloq*** au'
  },
  or: {
    en: 'or',
    fr: 'ou'
  },
  book1: {
    en: 'Choose a ***dedicated time slot***',
    fr: 'Préférez un ***créneau dédié*** pour'
  },
  book2: {
    en: 'to ***answer*** all your questions',
    fr: '***répondre*** à toutes vos questions et'
  },
  book3: {
    en: 'and ***kickstart*** your project now',
    fr: '***démarrer*** votre projet sans plus attendre'
  }
}

export function LBAction({ className, ifSmall = 'text', _I18N, langCode }: { className?: string, ifSmall?: 'hidden'|'icon'|'text', _I18N?: I18nDictionary, langCode: LangCode }) {

  const screen = useScreen()
  const i18n = getI18n(_I18N || I18N, langCode)
  const { openLBCalendarDialog } = useLBCalendarDialog(langCode)
  const koalendarUrl = getKoalendarUrl(langCode)
  const isIcon = () => {
    return ifSmall === 'icon' && !screen.sm
  }
  const isHidden = () => {
    return !koalendarUrl || ifSmall === 'hidden' && !screen.sm
  }

  return (isHidden() ? <></> :
    <Button className={cn('p-8 text-lg', className, isIcon() ? ' px-2' : '')}
      onClick={() => openLBCalendarDialog(koalendarUrl as string)}
      id='Call Booking'>
      {isIcon() ? <FaCalendarDays/> : i18n.action}
    </Button>
  )
}

export function useLBCallDialog() {
  const { openDialog } = useDialogActions()

  const openLBCallDialog = (langCode: LangCode) => {
    openDialog({ content: <LBCallDialogContent langCode={langCode}/> })
  }

  return { openLBCallDialog }
}

export function LBCallDialogContent({langCode}: {langCode: LangCode}) {
  const i18n = getI18n(I18N, langCode)
  return (
    <Card variant='consent' className='w-full p-1 sm:w-fit sm:min-w-[520px] sm:max-w-[520px] sm:p-4' style={{ margin: '20px' }}>
      <Flex className='justify-center gap-8 lg:gap-16'>
        <FlexCol className='flex-col items-center'>
          <MD className='pb-2 pt-4 text-center font-medium opacity-80 sm:text-lg' langCode={langCode}>{i18n.call}</MD>
          <MD className='pb-4 text-center font-medium opacity-80 sm:text-lg' langCode={langCode}>{'['+siteConfig.phone+']('+buildPhoneUrl(siteConfig.phone)+')'}</MD>
          <MD className='pb-4 text-center font-medium opacity-50' langCode={langCode}>{i18n.or}</MD>
          <MD className='pb-1 text-center font-medium opacity-80 sm:text-lg' langCode={langCode}>{i18n.book1}</MD>
          <MD className='pb-1 text-center font-medium opacity-80 sm:text-lg' langCode={langCode}>{i18n.book2}</MD>
          <MD className='pb-6 text-center font-medium opacity-80 sm:text-lg' langCode={langCode}>{i18n.book3}</MD>
          <LBAction langCode={langCode}/>
        </FlexCol>
      </Flex>
    </Card>
  )
}

export function LBCallAction({ className, langCode }: { className?: string, langCode: LangCode }) {
  const { openLBCallDialog } = useLBCallDialog()
  return (
    <Button className={cn('p-8 text-lg', className)} id='Call Legibloq'
      onClick={() => openLBCallDialog(langCode)}>
      <FaPhone/>
    </Button>
  )
}

export function LBBecomePartner({ className, langCode }: { className?: string, langCode: LangCode }) {

  const i18n = getI18n(I18N, langCode)
  const { openLBCalendarDialog } = useLBCalendarDialog(langCode)
  const koalendarUrl = getKoalendarUrl(langCode)

  return (!koalendarUrl ? <></> :
    <div className={cn('cursor-pointer font-medium text-primary hover:underline dark:text-primaryBright', className)}
      onClick={() => openLBCalendarDialog(koalendarUrl)}>
      {i18n.becomePartner}
    </div>
  )
}
